import type {
  OrderInput,
  OrderDetailInput,
  CargoExclusionInput,
  CreateOrderMutation,
  CreateOrderDetailMutation,
  CreateCargoExclusionMutation,
  OrderDetailPatch,
  OrderPatch,
  OrderRequirementAssociationInput,
  CreateOrderRequirementAssociationMutation,
  OrderAttributeAssociationInput,
  CreateOrderAttributeAssociationMutation,
  OrderForm_OrderAttributeAssociationFragment,
  GetDataToCleanupForOrderQueryResult,
  GetDataToCleanupForOrderQueryVariables,
  DeleteBillOfLadingDatumByIdMutationResult,
  DeleteBillOfLadingDatumByIdMutationVariables,
  OrderPortCallRelation,
  DeleteOrderPortCallRelationMutationResult,
  DeleteOrderPortCallRelationMutationVariables,
  DeleteOrderSpotRelationMutationResult,
  DeleteOrderSpotRelationMutationVariables,
  OrderVoyageCharterRelation,
  DeleteCargoMeasurementByIdMutationResult,
  DeleteCargoMeasurementByIdMutationVariables,
  DeleteCargoCompartmentAllocationByIdMutationResult,
  DeleteCargoCompartmentAllocationByIdMutationVariables,
  DeleteCargoExclusionByIdMutationResult,
  DeleteCargoExclusionByIdMutationVariables,
  DeleteNotificationReceiverByIdMutationResult,
  DeleteNotificationReceiverByIdMutationVariables,
  PlanningPageQuery,
  ScenarioTimeline_OrderFragment,
} from '../../graphql';
import {
  useCreateOrderMutation,
  useCreateOrderDetailMutation,
  useCreateCargoExclusionMutation,
  useUpdateOrderMutation,
  useUpdateOrderDetailMutation,
  useDeleteCargoExclusionByIdMutation,
  useCreateOrderRequirementAssociationMutation,
  useDeleteOrderRequirementAssociationMutation,
  useCreateOrderAttributeAssociationMutation,
  useDeleteOrderAttributeAssociationMutation,
  DeleteBillOfLadingDatumByIdDocument,
  DeleteOrderPortCallRelationDocument,
  GetDataToCleanupForOrderDocument,
  DeleteOrderSpotRelationDocument,
  DeleteCargoMeasurementByIdDocument,
  DeleteCargoCompartmentAllocationByIdDocument,
  DeleteCargoExclusionByIdDocument,
  DeleteNotificationReceiverByIdDocument,
  ScenarioTimeline_OrderFragmentDoc,
  MarginUnit,
} from '../../graphql';
import type { ExecutionResult, GraphQLError } from 'graphql';
import differenceWith from 'lodash/differenceWith';
import type { WithId } from '../../lib/util';
import {
  exhaustiveCheck,
  isDefined,
  missingOnDeleteIsOK,
  roundTo,
} from '../../lib/util';
import type { ApolloCache, ApolloClient } from '@apollo/client';
import { gql, useApolloClient } from '@apollo/client';
import type { Order } from '../core/Core';
import { nodeIdForParameterizedValue } from 'msand-apollo-cache-hermes/src/operations/SnapshotEditor';
import { useHermes } from '../../lib/init-apollo';
import type {
  ModifierDetails,
  Modifiers,
} from '@apollo/client/cache/core/types/common';
import type { ScenarioOrder } from '../../lib/planningData';

type Forbidden<T, K extends string> = T & { [K1 in K]?: undefined };

export const parseAmountInfo = (
  order: Pick<Order, 'marginAmount' | 'marginUnit' | 'cargoAmount'> & {
    cargoUnitByCargoUnitId?: { name?: string | null } | null;
    assortmentByAssortmentId?: Pick<
      NonNullable<ScenarioOrder['assortmentByAssortmentId']>,
      'marginAmount' | 'marginUnit'
    > | null;
  },
): string | undefined => {
  const {
    marginAmount = order.assortmentByAssortmentId?.marginAmount,
    marginUnit = order.assortmentByAssortmentId?.marginUnit,
    cargoUnitByCargoUnitId,
    cargoAmount,
  } = order;

  if (!cargoUnitByCargoUnitId) {
    return `${roundTo(cargoAmount, 3)}`;
  }

  const cargoUnitName = cargoUnitByCargoUnitId.name;
  const cargoAmountInfo = `${roundTo(cargoAmount, 3)} ${cargoUnitName}`;

  if (marginAmount && +marginAmount && marginUnit) {
    const roundedMarginAmount = roundTo(marginAmount, 3);
    switch (marginUnit) {
      case MarginUnit.MINMAX: {
        return `${roundTo(
          cargoAmount,
          3,
        )} - ${roundedMarginAmount} ${cargoUnitName}`;
      }
      case MarginUnit.PERCENTAGE: {
        return `${cargoAmountInfo} +/- ${roundedMarginAmount} %`;
      }
      case MarginUnit.TONNES: {
        return `${cargoAmountInfo} +/- ${roundedMarginAmount} ${cargoUnitName}`;
      }
      default: {
        exhaustiveCheck(marginUnit);
      }
    }
  }
  return cargoAmountInfo;
};

export type CreateOrderWithRelationsMutation = (
  newOrder: Forbidden<OrderInput, 'id'>,
  newOrderDetails: Forbidden<Omit<OrderDetailInput, 'orderId'>, 'id'>,
  cargoExclusions: Array<Omit<CargoExclusionInput, 'orderId'>>,
  specialRequirements: Omit<OrderRequirementAssociationInput, 'orderId'>[],
  orderAttributes: Omit<OrderAttributeAssociationInput, 'orderId'>[],
) => Promise<
  ExecutionResult<
    [
      CreateOrderMutation,
      CreateOrderDetailMutation,
      CreateCargoExclusionMutation[],
      CreateOrderRequirementAssociationMutation[],
      CreateOrderAttributeAssociationMutation[],
    ]
  >
>;

const flattenErrors = <T extends GraphQLError>(
  a: Array<{ errors?: readonly T[] }>,
): { errors: T[] } => {
  return { errors: a.flatMap((x) => x.errors).filter(isDefined) };
};

const storeFieldName = useHermes
  ? nodeIdForParameterizedValue('ROOT_QUERY', ['allOrders'], {
      condition: { softDelete: false, archived: false },
    })
  : 'allOrders({"condition":{"archived":false,"softDelete":false}})';

const fieldName = storeFieldName;

export function cacheAddNewOrders(
  newOrders: ScenarioTimeline_OrderFragment[],
  cache: ApolloCache<unknown>,
) {
  const modifiers: Modifiers<PlanningPageQuery> = {
    [fieldName]: (
      allOrders: PlanningPageQuery['allOrders'],
      details: ModifierDetails,
    ) => {
      if (details.storeFieldName !== storeFieldName) {
        return allOrders;
      }
      const nodes = (allOrders?.nodes ?? []).filter(isDefined);
      const ids = new Set(nodes.map((node) => cache.identify(node)));
      const newRefs = newOrders
        .map((o) => {
          cache.writeFragment({
            data: o,
            fragmentName: 'ScenarioTimeline_Order',
            fragment: ScenarioTimeline_OrderFragmentDoc,
          });
          return details.toReference(o);
        })
        .filter((ref) => ref && !ids.has(ref.__ref));
      return {
        ...allOrders,
        nodes: [...nodes, ...newRefs],
      };
    },
  };
  cache.modify({
    fields: modifiers,
  });
}

export function useCreateOrderWithRelationsMutation(): [
  CreateOrderWithRelationsMutation,
  { loading: boolean },
] {
  const client = useApolloClient();
  const [createOrder, { loading: createOrderLoading }] =
    useCreateOrderMutation();
  const [createDetails, { loading: createOrderDetailLoading }] =
    useCreateOrderDetailMutation();
  const [createExclusion, { loading: createCargoExclusionLoading }] =
    useCreateCargoExclusionMutation();
  const [
    createSpecialRequirement,
    { loading: createSpecialRequirementLoading },
  ] = useCreateOrderRequirementAssociationMutation();
  const [
    createOrderAttributeAssociation,
    { loading: createOrderAttributeAssociationLoading },
  ] = useCreateOrderAttributeAssociationMutation();

  const mut: CreateOrderWithRelationsMutation = async (
    newOrder,
    newOrderDetails,
    cargoExclusions,
    specialRequirements,
    orderAttributes,
  ) => {
    const res1 = await createOrder({
      variables: { input: { order: newOrder } },
    });

    if (!res1.data) {
      return { errors: res1.errors };
    }

    const order = res1.data.createOrder?.order;
    const orderId = order?.id;
    if (!orderId) {
      throw new Error(`No orderId ${orderId}`);
    }
    const res2 = await createDetails({
      variables: {
        input: {
          orderDetail: {
            ...newOrderDetails,
            orderId,
          },
        },
      },
    });

    const res3 = await Promise.all(
      cargoExclusions.map((exc) =>
        createExclusion({
          variables: {
            input: {
              cargoExclusion: {
                ...exc,
                orderId,
              },
            },
          },
        }),
      ),
    );

    const res4 = await Promise.all(
      specialRequirements.map((sr) =>
        createSpecialRequirement({
          variables: {
            input: {
              orderRequirementAssociation: {
                ...sr,
                orderId,
              },
            },
          },
        }),
      ),
    );

    const res5 = await Promise.all(
      orderAttributes.map((sr) =>
        createOrderAttributeAssociation({
          variables: {
            input: {
              orderAttributeAssociation: {
                ...sr,
                orderId,
              },
            },
          },
        }),
      ),
    );

    if (!res2.data) {
      return { errors: res2.errors };
    } else if (res3.some((r) => r.errors && r.errors.length > 0)) {
      return flattenErrors(res3);
    } else if (res4.some((r) => r.errors && r.errors.length > 0)) {
      return flattenErrors(res4);
    } else if (res5.some((r) => r.errors && r.errors.length > 0)) {
      return flattenErrors(res5);
    }

    const { cache } = client;

    const oraNodes = res4
      .map(
        (oRA) =>
          oRA.data?.createOrderRequirementAssociation
            ?.orderRequirementAssociation,
      )
      .filter(isDefined);

    const oaaNodes = res5
      .map(
        (oRA) =>
          oRA.data?.createOrderAttributeAssociation?.orderAttributeAssociation,
      )
      .filter(isDefined);

    const orderDetail = res2.data.createOrderDetail?.orderDetail;

    const fullOrder: NonNullable<PlanningPageQuery['allOrders']>['nodes']['0'] =
      {
        ...order,
        orderDetailsByOrderId: {
          nodes: [orderDetail].filter(isDefined),
        },
        orderRequirementAssociationsByOrderId: {
          nodes: oraNodes,
        },
        orderAttributeAssociationsByOrderId: {
          nodes: oaaNodes,
        },
      };

    cacheAddNewOrders([fullOrder], cache);

    return {
      errors: [],
      data: [
        res1.data,
        res2.data,
        res3.map((r) => r.data).filter(isDefined),
        res4.map((r) => r.data).filter(isDefined),
        res5.map((r) => r.data).filter(isDefined),
      ],
    };
  };

  return [
    mut,
    {
      loading:
        createOrderLoading ||
        createOrderDetailLoading ||
        createCargoExclusionLoading ||
        createSpecialRequirementLoading ||
        createOrderAttributeAssociationLoading,
    },
  ];
}

export type UpdateOrderWithRelationsMutation = (
  updatedOrder: WithId<OrderPatch>,
  updatedOrderDetails: OrderDetailPatch & { id?: number | null },
  newExclusions: Array<Omit<CargoExclusionInput, 'orderId'>>,
  oldExclusions: Array<Omit<WithId<CargoExclusionInput>, 'orderId'>>,
  newSpecialRequirements: Omit<OrderRequirementAssociationInput, 'orderId'>[],
  oldSpecialRequirements: Omit<
    WithId<OrderRequirementAssociationInput>,
    'orderId'
  >[],
  newOrderAttributes: Omit<OrderAttributeAssociationInput, 'orderId'>[],
  oldOrderAttributes: Omit<WithId<OrderAttributeAssociationInput>, 'orderId'>[],
) => Promise<ExecutionResult<[]>>;

const isSameExlusion = (
  a: Partial<CargoExclusionInput>,
  b: Partial<CargoExclusionInput>,
): boolean =>
  a.assortmentGroup === b.assortmentGroup &&
  a.distance == b.distance &&
  a.tanks == b.tanks &&
  a.voyages == b.voyages;

export type UpdateOrderAttributesMutation = (
  orderId: number,
  newOrderAttributes: Omit<OrderAttributeAssociationInput, 'orderId'>[],
  oldOrderAttributes: Omit<WithId<OrderAttributeAssociationInput>, 'orderId'>[],
) => Promise<ExecutionResult<[]>>;

export function useUpdateOrderAttributesMutation(): [
  UpdateOrderAttributesMutation,
  { loading: boolean },
] {
  const [
    createOrderAttributeAssociation,
    { loading: createOrderAttributeAssociationLoading },
  ] = useCreateOrderAttributeAssociationMutation();
  const [
    deleteOrderAttributeAssociation,
    { loading: deleteOrderAttributeAssociationByIdLoading },
  ] = useDeleteOrderAttributeAssociationMutation();

  const mut: UpdateOrderAttributesMutation = async (
    orderId,
    newAttributes,
    oldAttributes,
  ) => {
    const attributesToDelete = differenceWith(
      oldAttributes,
      newAttributes,
      (a, b) => a.orderAttributeId === b.orderAttributeId,
    );
    const attributesToCreate = differenceWith(
      newAttributes,
      oldAttributes,
      (a, b) => a.orderAttributeId === b.orderAttributeId,
    );

    const res = await Promise.all([
      Promise.all(
        attributesToDelete.map((sr) =>
          deleteOrderAttributeAssociation({
            variables: { input: { id: sr.id } },
            update: (cache, { data }) => {
              const idToDelete =
                data?.deleteOrderAttributeAssociationById
                  ?.orderAttributeAssociation?.id;

              if (!idToDelete) return;

              cache.modify({
                id: cache.identify({
                  __typename: 'Order',
                  id: orderId,
                }),
                fields: {
                  orderAttributeAssociationsByOrderId: (
                    existingRefs,
                    { readField },
                  ) => ({
                    nodes: existingRefs.nodes.filter(
                      (node: OrderForm_OrderAttributeAssociationFragment) =>
                        readField('id', node) !== idToDelete,
                    ),
                  }),
                },
              });
            },
          }),
        ),
      ).then(flattenErrors),
      Promise.all(
        attributesToCreate.map((sr) =>
          createOrderAttributeAssociation({
            variables: {
              input: {
                orderAttributeAssociation: {
                  ...sr,
                  orderId,
                },
              },
            },
            update: (cache, { data }) => {
              const newAssociation =
                data?.createOrderAttributeAssociation
                  ?.orderAttributeAssociation;

              if (!newAssociation) return;

              const newAttrRelRef = cache.writeFragment({
                data: newAssociation,
                fragment: gql`
                  fragment NewAssociation on OrderAttributeAssociation {
                    id
                    orderId
                    orderAttributeId
                  }
                `,
              });

              cache.modify({
                id: cache.identify({
                  __typename: 'Order',
                  id: orderId,
                }),
                fields: {
                  orderAttributeAssociationsByOrderId: (existingRefs) => ({
                    nodes: [...existingRefs.nodes, newAttrRelRef],
                  }),
                },
              });
            },
          }),
        ),
      ).then(flattenErrors),
    ]);

    return {
      errors: flattenErrors(res).errors,
      data: [],
    };
  };

  return [
    mut,
    {
      loading:
        createOrderAttributeAssociationLoading ||
        deleteOrderAttributeAssociationByIdLoading,
    },
  ];
}

export function useUpdateOrderWithRelationsMutation(): [
  UpdateOrderWithRelationsMutation,
  { loading: boolean },
] {
  const [updateOrder, { loading: updateOrderLoading }] =
    useUpdateOrderMutation();
  const [updateDetails, { loading: updateOrderDetailLoading }] =
    useUpdateOrderDetailMutation();
  const [createDetails, { loading: createOrderDetailLoading }] =
    useCreateOrderDetailMutation();
  const [createExclusion, { loading: createCargoExclusionLoading }] =
    useCreateCargoExclusionMutation();
  const [deleteExclusion, { loading: deleteCargoExclusionByIdLoading }] =
    useDeleteCargoExclusionByIdMutation();
  const [
    createSpecialRequirement,
    { loading: createSpecialRequirementLoading },
  ] = useCreateOrderRequirementAssociationMutation();
  const [
    deleteSpecialRequirement,
    { loading: deleteSpecialRequirementByIdLoading },
  ] = useDeleteOrderRequirementAssociationMutation();
  const [updateOrderAttributes, { loading: updateOrderAttributesLoading }] =
    useUpdateOrderAttributesMutation();

  const mut: UpdateOrderWithRelationsMutation = async (
    updatedOrder,
    updatedOrderDetails,
    newExclusions,
    oldExclusions,
    newRequirements,
    oldRequirements,
    newAttributes,
    oldAttributes,
  ) => {
    const exclusionsToDelete = differenceWith(
      oldExclusions,
      newExclusions,
      isSameExlusion,
    );
    const exclusionsToCreate = differenceWith(
      newExclusions,
      oldExclusions,
      isSameExlusion,
    );

    const requirementsToDelete = differenceWith(
      oldRequirements,
      newRequirements,
      (a, b) => a.specialRequirementId === b.specialRequirementId,
    );
    const requirementsToCreate = differenceWith(
      newRequirements,
      oldRequirements,
      (a, b) => a.specialRequirementId === b.specialRequirementId,
    );

    const { id: updatedOrderId, ...orderPatch } = updatedOrder;
    const { id: updatedOrderDetailsId, ...orderDetailPatch } =
      updatedOrderDetails;
    const res = await Promise.all([
      await updateOrder({
        variables: { input: { id: updatedOrderId, orderPatch } },
      }),

      updatedOrderDetailsId
        ? await updateDetails({
            variables: {
              input: {
                id: updatedOrderDetailsId,
                orderDetailPatch,
              },
            },
          })
        : await createDetails({
            variables: {
              input: {
                orderDetail: {
                  ...orderDetailPatch,
                  orderId: updatedOrder.id,
                },
              },
            },
          }),
      Promise.all(
        exclusionsToDelete.map((exc) =>
          deleteExclusion({ variables: { input: { id: exc.id } } }),
        ),
      ).then(flattenErrors),
      Promise.all(
        exclusionsToCreate.map((exc) =>
          createExclusion({
            variables: {
              input: {
                cargoExclusion: {
                  ...exc,
                  orderId: updatedOrder.id,
                },
              },
            },
          }),
        ),
      ).then(flattenErrors),
      Promise.all(
        requirementsToDelete.map((sr) =>
          deleteSpecialRequirement({ variables: { input: { id: sr.id } } }),
        ),
      ).then(flattenErrors),
      Promise.all(
        requirementsToCreate.map((sr) =>
          createSpecialRequirement({
            variables: {
              input: {
                orderRequirementAssociation: {
                  ...sr,
                  orderId: updatedOrder.id,
                },
              },
            },
          }),
        ),
      ).then(flattenErrors),
      updateOrderAttributes(updatedOrder.id, newAttributes, oldAttributes),
    ]);

    return {
      errors: flattenErrors(res).errors,
      data: [],
    };
  };

  return [
    mut,
    {
      loading:
        updateOrderLoading ||
        createOrderDetailLoading ||
        updateOrderDetailLoading ||
        createCargoExclusionLoading ||
        deleteCargoExclusionByIdLoading ||
        createSpecialRequirementLoading ||
        deleteSpecialRequirementByIdLoading ||
        updateOrderAttributesLoading,
    },
  ];
}

export const getOrderDisplayName = (
  order:
    | Pick<ScenarioTimeline_OrderFragment, 'id' | 'orderDetailsByOrderId'>
    | null
    | undefined,
): string => {
  if (!order) {
    return 'NO ID';
  }
  const orderName = order.orderDetailsByOrderId.nodes[0]?.orderName;
  return `#${order.id}${orderName ? ' ' + orderName : ''}`;
};

async function deleteOrderPortCallRelation(
  client: ApolloClient<unknown>,
  relation: Pick<OrderPortCallRelation, 'id'>,
) {
  await client
    .mutate<
      DeleteOrderPortCallRelationMutationResult['data'],
      DeleteOrderPortCallRelationMutationVariables
    >({
      mutation: DeleteOrderPortCallRelationDocument,
      variables: {
        input: {
          id: relation.id,
        },
      },
      errorPolicy: 'all',
    })
    .then(missingOnDeleteIsOK(`port call relation ${relation.id}`));
}

async function cleanupOrderRelations(
  client: ApolloClient<unknown>,
  orderId: number,
): Promise<void> {
  const data = await client.query<
    GetDataToCleanupForOrderQueryResult['data'],
    GetDataToCleanupForOrderQueryVariables
  >({
    query: GetDataToCleanupForOrderDocument,
    variables: { orderId },
  });

  const bolDatumId =
    data.data?.orderById?.billOfLadingDataByOrderId?.nodes.at(0)?.id;
  const portCallRelationIds =
    data.data?.orderById?.orderPortCallRelationsByOrderId.nodes
      .map((n) => n?.id)
      .filter(isDefined) ?? [];
  const voyageCharterRelationIds =
    data.data?.orderById?.orderVoyageCharterRelationsByOrderId?.nodes
      .filter(isDefined)
      .map((rel) => rel.id) ?? [];
  const cargoMeasurementIds =
    data.data?.orderById?.cargoMeasurementsByOrderId?.nodes
      .filter(isDefined)
      .map((rel) => rel.id) ?? [];
  const cargoCompartmentAllocationIds =
    data.data?.orderById?.cargoCompartmentAllocationsByOrderId?.nodes
      .filter(isDefined)
      .map((rel) => rel.id) ?? [];

  const ready: Promise<unknown>[] = [];

  if (bolDatumId !== undefined) {
    ready.push(
      client
        .mutate<
          DeleteBillOfLadingDatumByIdMutationResult['data'],
          DeleteBillOfLadingDatumByIdMutationVariables
        >({
          mutation: DeleteBillOfLadingDatumByIdDocument,
          variables: { bolDatumId },
          errorPolicy: 'all',
        })
        .then(missingOnDeleteIsOK(`DeleteBillOfLadingDatumById ${bolDatumId}`)),
    );
  }

  // At the moment seems excessive as port call relations are already handled by another method but seems better
  // to collect all cleanup here.
  for (const pcRelId of portCallRelationIds) {
    ready.push(deleteOrderPortCallRelation(client, { id: pcRelId }));
  }

  for (const vcRelId of voyageCharterRelationIds) {
    ready.push(
      client
        .mutate<
          DeleteOrderSpotRelationMutationResult['data'],
          DeleteOrderSpotRelationMutationVariables
        >({
          mutation: DeleteOrderSpotRelationDocument,
          variables: { input: { id: vcRelId } },
          errorPolicy: 'all',
          update: (cache, { data }) => {
            const deletedRel =
              data?.deleteOrderVoyageCharterRelationById
                ?.orderVoyageCharterRelation;

            if (!deletedRel) return;

            cache.modify({
              id: cache.identify({
                __typename: 'Order',
                id: deletedRel.orderId,
              }),
              fields: {
                orderVoyageCharterRelationsByOrderId: (existingRefs) => ({
                  nodes: existingRefs.nodes.filter(
                    (ref: OrderVoyageCharterRelation) =>
                      ref.id !== deletedRel.id,
                  ),
                }),
              },
            });
          },
        })
        .then(missingOnDeleteIsOK(`DeleteOrderSpotRelation ${vcRelId}`)),
    );
  }

  for (const id of cargoMeasurementIds) {
    ready.push(
      client
        .mutate<
          DeleteCargoMeasurementByIdMutationResult['data'],
          DeleteCargoMeasurementByIdMutationVariables
        >({
          mutation: DeleteCargoMeasurementByIdDocument,
          variables: { cargoMeasurementId: id },
          errorPolicy: 'all',
        })
        .then(missingOnDeleteIsOK(`DeleteCargoMeasurementById ${id}`)),
    );
  }

  for (const id of cargoCompartmentAllocationIds) {
    ready.push(
      client
        .mutate<
          DeleteCargoCompartmentAllocationByIdMutationResult['data'],
          DeleteCargoCompartmentAllocationByIdMutationVariables
        >({
          mutation: DeleteCargoCompartmentAllocationByIdDocument,
          variables: { cargoCompartmentAllocationId: id },
          errorPolicy: 'all',
        })
        .then(
          missingOnDeleteIsOK(`DeleteCargoCompartmentAllocationById ${id}`),
        ),
    );
  }

  await Promise.all(ready);
}

export async function cleanupOrderForDeletion(
  client: ApolloClient<unknown>,
  orderId: number,
): Promise<void> {
  const data = await client.query<
    GetDataToCleanupForOrderQueryResult['data'],
    GetDataToCleanupForOrderQueryVariables
  >({
    query: GetDataToCleanupForOrderDocument,
    variables: { orderId },
  });

  const order = data.data?.orderById;
  if (!order) throw new Error(`Could not query order ${orderId}} for cleanup`);

  if (order.scheduledOrdersByOrderId?.nodes.length > 0) {
    throw new Error(`Order ${orderId} has scheduled orders, cannot delete`);
  } else if (isDefined(order.handledByCoaByOrderId)) {
    throw new Error(`Order ${orderId} has handled by COA, cannot delete`);
  } else if (isDefined(order.handledBySpotByOrderId)) {
    throw new Error(`Order ${orderId} has handled by Spot, cannot delete`);
  }

  const cargoExclusionIds =
    order.cargoExclusionsByOrderId?.nodes
      .filter(isDefined)
      .map((rel) => rel.id) ?? [];
  const notificationReceiverIds =
    order.notificationReceiversByOrderId?.nodes
      .filter(isDefined)
      .map((rel) => rel.id) ?? [];

  const ready: Promise<unknown>[] = [];

  // Just to be sure in case for whatever reason any relations still remain after it was unassigned before
  ready.push(cleanupOrderRelations(client, orderId));

  for (const id of cargoExclusionIds) {
    ready.push(
      client
        .mutate<
          DeleteCargoExclusionByIdMutationResult['data'],
          DeleteCargoExclusionByIdMutationVariables
        >({
          mutation: DeleteCargoExclusionByIdDocument,
          variables: { input: { id } },
          errorPolicy: 'all',
        })
        .then(missingOnDeleteIsOK(`DeleteCargoExclusionById ${id}`)),
    );
  }

  for (const id of notificationReceiverIds) {
    ready.push(
      client
        .mutate<
          DeleteNotificationReceiverByIdMutationResult['data'],
          DeleteNotificationReceiverByIdMutationVariables
        >({
          mutation: DeleteNotificationReceiverByIdDocument,
          variables: { notificationReceiverId: id },
          errorPolicy: 'all',
        })
        .then(missingOnDeleteIsOK(`DeleteNotificationReceiverById ${id}`)),
    );
  }

  await Promise.all(ready);
}
